// ---------------------------------------------------------------------------
// Scoring tree utilities — shared between ScoringPanel and VideoPlayer so
// that VideoPlayer can compute activeStartTime without passing currentTime
// all the way down into ScoringPanel (which would cause ScoringPanel to
// re-render on every video frame).
// ---------------------------------------------------------------------------

function normalizeScore(score) {
  const parts = score.split('-').map(Number);
  if (parts.length !== 2 || parts.some(isNaN)) return score;
  const [a, b] = parts;
  return a >= b ? `${a}-${b}` : `${b}-${a}`;
}

// Convert a single score token (tennis or numeric) to an integer for comparison.
// 0→0, 15→1, 30→2, 40→3, A/AD→4, any other integer→itself.
function parsePart(s) {
  const map = { '0': 0, '15': 1, '30': 2, '40': 3, 'A': 4, 'AD': 4 };
  if (map[s] !== undefined) return map[s];
  const n = parseInt(s, 10);
  return isNaN(n) ? null : n;
}

/**
 * Determine who won a point from two consecutive point scores.
 * Handles both standard tennis notation (0/15/30/40/A) and the numeric fallback
 * format (plain integers) produced by advanceScoreExtended.
 * Returns 'server' | 'receiver' | null (null when outcome can't be inferred).
 */
export function getOutcome(fromScore, toScore) {
  if (!fromScore || !toScore) return null;
  const [fs, fr] = fromScore.split('-').map(parsePart);
  const [ts, tr] = toScore.split('-').map(parsePart);
  if (fs === null || fr === null || ts === null || tr === null) return null;
  // Numeric comparison handles 40-40→A/AD-40 and 40-40→40-A/AD automatically
  // (parsePart maps both 'A' and 'AD' to 4, which is > 3 for '40').
  if (ts > fs) return 'server';
  if (tr > fr) return 'receiver';
  // Advantage → deuce: score decreases, can't be caught by numeric comparison.
  // Handle both 'A' (old format) and 'AD' (newer format) notation.
  const isServerAdv   = (s) => s === 'A-40' || s === 'AD-40';
  const isReceiverAdv = (s) => s === '40-A' || s === '40-AD';
  if (isServerAdv(fromScore))   return toScore === '40-40' ? 'receiver' : 'server';
  if (isReceiverAdv(fromScore)) return toScore === '40-40' ? 'server'   : 'receiver';
  return null;
}

/**
 * Advance a tennis point score given who won the point.
 * Returns the new score string, or null if the game ends (winner reached 40/Ad).
 */
export function advanceScore(score, outcome) {
  if (!outcome || !score) return null;
  const pts = ['0', '15', '30', '40'];
  const [s, r] = score.split('-');
  // Accept both 'A' (legacy) and 'AD' (newer datasets) as the advantage token.
  const isAdv      = (t) => t === 'A' || t === 'AD';
  const validToken = (t) => pts.includes(t) || isAdv(t);
  // Non-tennis tokens → return null so advanceScoreExtended uses numeric fallback.
  if (!validToken(s) || !validToken(r)) return null;
  // Mirror the advantage format already present in the score; default to 'AD'
  // for "40-40" (deuce) since newer data uses that convention.
  const advToken = (s === 'A' || r === 'A') ? 'A' : 'AD';
  if (outcome === 'server') {
    if (isAdv(s)) return null;                              // server wins game
    if (s === '40' && isAdv(r)) return '40-40';            // receiver had adv → deuce
    if (s === '40' && r === '40') return `${advToken}-40`; // deuce → server adv
    if (s === '40') return null;                            // server wins game
    return `${pts[pts.indexOf(s) + 1]}-${r}`;
  } else {
    if (isAdv(r)) return null;                              // receiver wins game
    if (isAdv(s) && r === '40') return '40-40';            // server had adv → deuce
    if (s === '40' && r === '40') return `40-${advToken}`; // deuce → receiver adv
    if (r === '40') return null;                            // receiver wins game
    return `${s}-${pts[pts.indexOf(r) + 1]}`;
  }
}

export const opp = (p) => (p === 'top' ? 'bottom' : 'top');

/**
 * Determine who won Game N (the last point's winner) by comparing the raw
 * (server-perspective) game scores of consecutive games, accounting for the
 * perspective switch that happens when servers alternate.
 * Returns 'server' | 'receiver' (from Game N's server's perspective) | null.
 */
export function computeGameWinner(game, nextGame) {
  if (!game || !nextGame) return null;
  const parseRaw = (raw) => raw.split('-').map(n => parseInt(n, 10));

  // Absolute counts at the start of Game N
  const [lN, rN] = parseRaw(game.rawGameScore);
  const topN    = game.serverPlayer === 'top'    ? lN : rN;
  const bottomN = game.serverPlayer === 'bottom' ? lN : rN;

  // Absolute counts at the start of Game N+1
  const [lN1, rN1] = parseRaw(nextGame.rawGameScore);
  const topN1    = nextGame.serverPlayer === 'top'    ? lN1 : rN1;
  const bottomN1 = nextGame.serverPlayer === 'bottom' ? lN1 : rN1;

  const winnerPlayer = topN1 > topN ? 'top' : bottomN1 > bottomN ? 'bottom' : null;
  if (!winnerPlayer) return null;
  return winnerPlayer === game.serverPlayer ? 'server' : 'receiver';
}

/**
 * Compute the new raw game score for Game N+1 when the winner of Game N is
 * changed to `newOutcome` ('server'|'receiver' from Game N's perspective).
 * Returns a raw score string like "1-0" ready to write onto annotations.
 */
export function computeNewNextGameScore(game, nextGame, newOutcome) {
  const winnerPlayer = newOutcome === 'server' ? game.serverPlayer : opp(game.serverPlayer);
  const parseRaw = (raw) => raw.split('-').map(n => parseInt(n, 10));

  const [lN, rN] = parseRaw(game.rawGameScore);
  const topN    = game.serverPlayer === 'top'    ? lN : rN;
  const bottomN = game.serverPlayer === 'bottom' ? lN : rN;

  const newTopGames    = topN    + (winnerPlayer === 'top'    ? 1 : 0);
  const newBottomGames = bottomN + (winnerPlayer === 'bottom' ? 1 : 0);

  const newLeft  = nextGame.serverPlayer === 'top'    ? newTopGames    : newBottomGames;
  const newRight = nextGame.serverPlayer === 'top'    ? newBottomGames : newTopGames;
  return `${newLeft}-${newRight}`;
}

/**
 * Like advanceScore but with a numeric fallback when the game would normally
 * end. Converts 0→0, 15→1, 30→2, 40→3, A→4 and continues counting from there
 * (e.g. 40-30 + server-wins → 4-2, then 5-2, 6-2, …).
 */
export function advanceScoreExtended(score, outcome) {
  const result = advanceScore(score, outcome);
  if (result !== null) return result;
  // Numeric fallback
  const parsePart = (s) => {
    const map = { '0': 0, '15': 1, '30': 2, '40': 3, 'A': 4, 'AD': 4 };
    if (map[s] !== undefined) return map[s];
    const n = parseInt(s, 10);
    return isNaN(n) ? 0 : n;
  };
  const [s, r] = score.split('-').map(parsePart);
  return outcome === 'server' ? `${s + 1}-${r}` : `${s}-${r + 1}`;
}

/**
 * Determine who won Set N by comparing the raw set scores of consecutive sets,
 * accounting for the perspective switch when the first server changes between sets.
 * Returns 'top' | 'bottom' (absolute player) | null.
 */
export function computeSetWinner(set, nextSet) {
  if (!set || !nextSet) return null;
  const parseRaw = (raw) => raw.split('-').map(n => parseInt(n, 10));

  const [lN, rN] = parseRaw(set.rawSetScore);
  const topN    = set.serverPlayer === 'top'    ? lN : rN;
  const bottomN = set.serverPlayer === 'bottom' ? lN : rN;

  const [lN1, rN1] = parseRaw(nextSet.rawSetScore);
  const topN1    = nextSet.serverPlayer === 'top'    ? lN1 : rN1;
  const bottomN1 = nextSet.serverPlayer === 'bottom' ? lN1 : rN1;

  if (topN1    > topN)    return 'top';
  if (bottomN1 > bottomN) return 'bottom';
  return null;
}

/**
 * Build the Set → Game → Point tree from the annotation array.
 */
export function buildScoringTree(annotations) {
  const strokes = annotations.filter(
    (a) =>
      a.type === 'stroke' &&
      a.pointScore && a.pointScore !== 'None' &&
      a.gameScore  && a.gameScore  !== 'None' &&
      a.setScore   && a.setScore   !== 'None'
  );

  if (strokes.length === 0) return [];

  const sorted = [...strokes].sort((a, b) => a.timestamp - b.timestamp);

  const sets = [];
  let curSet = null, curGame = null, curPoint = null;
  let curSetScore = null, curGameScore = null, curPointScore = null;
  let curPointLastTime = null;
  let curPointPrevTime = null;

  for (const stroke of sorted) {
    const setScore  = normalizeScore(stroke.setScore);
    const gameScore = normalizeScore(stroke.gameScore);
    const { pointScore, timestamp } = stroke;

    if (setScore !== curSetScore) {
      // Store rawSetScore + serverPlayer of the first stroke so computeSetWinner
      // can determine who won the set by comparing consecutive set objects.
      curSet = { setScore, rawSetScore: stroke.setScore, serverPlayer: stroke.player, games: [] };
      sets.push(curSet);
      curSetScore = setScore;
      curGameScore = curPointScore = null;
      curGame = curPoint = null;
    }

    if (gameScore !== curGameScore) {
      curGame = { gameScore, rawGameScore: stroke.gameScore, setScore, points: [], serverPlayer: stroke.player };
      curSet.games.push(curGame);
      curGameScore = gameScore;
      curPointScore = null;
      curPoint = null;
    }

    if (pointScore !== curPointScore) {
      curPoint = {
        pointScore, gameScore, setScore,
        startTime: timestamp, endTime: timestamp,
      };
      curGame.points.push(curPoint);
      curPointScore = pointScore;
      curPointLastTime = timestamp;
      curPointPrevTime = null;
    } else {
      curPointPrevTime = curPointLastTime;
      curPointLastTime = timestamp;
      curPoint.endTime = curPointPrevTime;
    }
  }

  return sets;
}

/**
 * Flatten the tree into a sorted list of point objects — useful for binary
 * search when computing the active point from currentTime.
 */
export function flattenPoints(tree) {
  return tree.flatMap((s) => s.games.flatMap((g) => g.points));
}

/**
 * Return the startTime of the rally that is active at `currentTime`.
 * Returns null if no rally has started yet.
 */
export function getActiveStartTime(sortedPoints, currentTime) {
  let active = null;
  for (const p of sortedPoints) {
    if (p.startTime <= currentTime) active = p.startTime;
    else break;
  }
  return active;
}
